import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import type { AgentSourceRecord, BuilderPlan } from "@yagni/shared";
import { Markdown } from "../components/Markdown.js";
import { SKILLS_LOADED_LABEL, agentSourceLine } from "../lib/agentSourcesCopy.js";
import { formatRelative } from "../lib/relativeTime.js";
import { Fold, hhmm } from "./LiveStream.js";
import {
  BUILD_ENVIRONMENT_LINES,
  buildEnvironmentLine,
  buildEnvironmentRecord,
  type BuildEnvironmentFallbackReason,
  type BuildEnvironmentSource,
} from "./buildEnvironment.js";

type JsonObject = Record<string, unknown>;

interface MissionVerification {
  id?: string;
  criterionId: string;
  kind: string;
  status: "passed" | "failed" | "inconclusive" | "not_run";
  summary: string;
  observedAt: string;
  authority: "authoritative" | "advisory";
  observer: string;
  verifierProfile: string;
  evidenceRef: string | null;
}

interface MissionArtifactRevision {
  id: string;
  revision: number;
  contentHash: string;
  providerVersion: string | null;
  preview: { kind: "markdown" | "table" | "link" | "json"; data: unknown } | null;
  citations: Array<{
    id: string;
    sourceRef: { label: string | null; url: string | null; sourceSystem: string };
    locator: string | null;
    claim: string | null;
  }>;
  verification: MissionVerification[];
  consequenceIds: string[];
  undo: { kind: string; expiresAt: string | null; payload: JsonObject } | null;
  createdAt: string;
}

interface MissionArtifact {
  id?: string;
  type?: string;
  kind?: string;
  title?: string;
  status?: string;
  sourceSystem?: string;
  externalId?: string | null;
  url?: string | null;
  currentRevisionId?: string;
  [key: string]: unknown;
}

interface MissionStep {
  id: string;
  sequence: number;
  stage: string;
  kind: string;
  status: string;
  capability?: string;
  sanitizedResult?: unknown;
  startedAt?: string;
  finishedAt?: string;
}

interface MissionPacket {
  status: "draft" | "awaiting_approval" | "approved" | "superseded";
  approvedBy: string | null;
  approvedAt: string | null;
  sha256: string;
  packet: {
    identity: { teamName: string };
    intent: { goal: string; rationale: string };
    plan: {
      summary: string;
      steps: Array<{ id: string; stage: string; description: string }>;
      acceptanceCriteria: Array<{ id: string; description: string; required: boolean }>;
      expectedArtifacts: Array<{ id: string; type: string; title: string; required: boolean }>;
      capabilityEnvelope: Array<{
        id: string;
        capabilityId: string;
        effect: string;
        sourceSystem?: string | null;
      }>;
    };
    execution: {
      anticipatedConsequences: Array<{ kind: string; description: string }>;
    };
  };
}

export interface WorkMissionView {
  schemaVersion: 1;
  source: "mission" | "legacy";
  workItemId: string;
  run: {
    id: string;
    attemptNo: number;
    kind: string;
    status: string;
    outcome: string | null;
    stopReason: string | null;
    outcomeSummary: string | null;
    startedAt: string;
    finishedAt: string | null;
  };
  packet: MissionPacket | null;
  plan: MissionPacket["packet"]["plan"] | null;
  legacyPlan?: Pick<BuilderPlan, "summary" | "steps" | "filesToChange" | "acceptanceCriteria" | "testStrategy"> | null;
  steps: MissionStep[];
  outputs: Array<{
    artifact: MissionArtifact;
    revision: MissionArtifactRevision | null;
    sourceKind: "run_artifact" | "legacy_run_artifact";
    sourceId: string;
  }>;
  activity: Array<{
    event: JsonObject;
    sourceKind: "run_event" | "coding_event";
    sourceId: string;
  }>;
  receipts: Array<{
    receipt: JsonObject;
    sourceKind: "run_receipt" | "legacy_run";
    sourceId: string;
  }>;
  /**
   * Which repository environment the build installed, as the run recorded it.
   * Null (or absent, from an older server) when the run recorded none.
   */
  environment?: {
    source: BuildEnvironmentSource;
    revisionId: string | null;
    fallbackReason?: BuildEnvironmentFallbackReason;
  } | null;
  /**
   * Where the build's skills came from (YAG-784): the trusted repository, the
   * workspace's plugin marketplace, a person's own skills repository, each
   * with the commit it was read at, or why it was not loaded. Absent from an
   * older server or a run that recorded none.
   */
  agentSources?: AgentSourceRecord[] | null;
}


export function useWorkMissionView(
  workItemId: string,
  runId: string | null = null,
  enabled = true,
): {
  view: WorkMissionView | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
} {
  const [view, setView] = useState<WorkMissionView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestSequence = useRef(0);
  const activeController = useRef<AbortController | null>(null);

  const fetchView = useCallback((background = false) => {
    if (background && activeController.current) return;
    const requestId = ++requestSequence.current;
    activeController.current?.abort();
    activeController.current = null;
    if (!enabled) {
      setLoading(false);
      setError(false);
      setView(null);
      return;
    }

    const controller = new AbortController();
    activeController.current = controller;
    if (!background) {
      setLoading(true);
      setError(false);
      setView(null);
    }
    const query = runId ? `?runId=${encodeURIComponent(runId)}` : "";
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/mission-view${query}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`mission view fetch failed: ${response.status}`);
        const payload = (await response.json()) as unknown;
        if (background && !isMissionView(payload)) throw new Error("Invalid work record response");
        return isMissionView(payload) ? payload : null;
      })
      .then((nextView) => {
        if (requestSequence.current === requestId && !controller.signal.aborted) {
          setView(nextView);
        }
      })
      .catch(() => {
        if (requestSequence.current === requestId && !controller.signal.aborted) {
          if (!background) {
            setView(null);
            setError(true);
          } else {
            console.warn("Work record refresh failed; retaining the last loaded record.");
          }
        }
      })
      .finally(() => {
        if (requestSequence.current === requestId && !controller.signal.aborted) {
          activeController.current = null;
          setLoading(false);
        }
      });
  }, [enabled, workItemId, runId]);
  const reload = useCallback(() => fetchView(), [fetchView]);

  useEffect(() => {
    reload();
    return () => {
      requestSequence.current += 1;
      activeController.current?.abort();
      activeController.current = null;
    };
  }, [reload]);
  const live = view?.run.status === "running" || view?.run.status === "scoping";
  useEffect(() => {
    if (!enabled || !live) return;
    const timer = setInterval(() => fetchView(true), 15_000);
    return () => clearInterval(timer);
  }, [enabled, live, fetchView]);
  return { view, loading, error, reload };
}

function isMissionView(value: unknown): value is WorkMissionView {
  if (!isObject(value)) return false;
  return value.schemaVersion === 1 && isObject(value.run) && Array.isArray(value.outputs);
}

export function Plan({ view }: { view: WorkMissionView }) {
  const plan = view.plan;
  if (!plan) {
    const legacyPlan = view.legacyPlan;
    if (!legacyPlan) return <p className="cf-mission-empty">No structured plan is recorded for this run.</p>;
    return (
      <div className="cf-mission-stack cf-mission-legacy-plan" data-testid="mission-plan">
        <Markdown className="cf-mission-summary" text={legacyPlan.summary} />
        <MissionGroup title="Steps">
          <ol className="cf-mission-list">
            {legacyPlan.steps.map((step, index) => (
              <li key={index}>
                {step.description}
                {[...step.files, ...step.testFiles].length > 0 && (
                  <div className="cf-mission-tags">
                    {[...new Set([...step.files, ...step.testFiles])].map((file) => <span key={file}>{file}</span>)}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </MissionGroup>
        {legacyPlan.filesToChange.length > 0 && (
          <MissionGroup title="Files to change">
            <ul className="cf-mission-list">{legacyPlan.filesToChange.map((file) => <li key={file}>{file}</li>)}</ul>
          </MissionGroup>
        )}
        {legacyPlan.acceptanceCriteria.length > 0 && (
          <MissionGroup title="Acceptance criteria">
            <ul className="cf-mission-list">{legacyPlan.acceptanceCriteria.map((criterion, index) => <li key={index}>{criterion}</li>)}</ul>
          </MissionGroup>
        )}
        {legacyPlan.testStrategy && <MissionGroup title="Test strategy"><Markdown text={legacyPlan.testStrategy} /></MissionGroup>}
      </div>
    );
  }
  return (
    <div className="cf-mission-stack" data-testid="mission-plan">
      <Markdown className="cf-mission-summary" text={plan.summary} />
      <MissionGroup title="Steps">
        <ol className="cf-mission-steps">
          {plan.steps.map((step, index) => {
            // Packet step ids describe the approved plan; persisted execution
            // rows receive their own database ids. Sequence is their stable
            // join inside a Run.
            const execution = view.steps.find((record) => record.sequence === index + 1);
            return (
              <li key={step.id}>
                <span className={`cf-mission-state is-${execution?.status ?? "planned"}`}>
                  {execution ? stepStatus(execution.status) : "Planned"}
                </span>
                <span><b>{stageLabel(step.stage)}</b>{step.description}</span>
              </li>
            );
          })}
        </ol>
      </MissionGroup>
      {plan.expectedArtifacts.length > 0 && (
        <MissionGroup title="Intended outputs">
          <ul className="cf-mission-list">
            {plan.expectedArtifacts.map((artifact) => (
              <li key={artifact.id}>{artifact.title} · {artifactLabel(artifact.type)}{artifact.required ? "" : " (optional)"}</li>
            ))}
          </ul>
        </MissionGroup>
      )}
      {plan.capabilityEnvelope.length > 0 && (
        <MissionGroup title="Approved capabilities">
          <div className="cf-mission-tags">
            {plan.capabilityEnvelope.map((capability) => (
              <span key={capability.id}>{capabilityLabel(capability.capabilityId)} · {capability.effect}</span>
            ))}
          </div>
        </MissionGroup>
      )}
      {(view.packet?.packet.execution.anticipatedConsequences.length ?? 0) > 0 && (
        <MissionGroup title="Consequences requiring review">
          <ul className="cf-mission-list">
            {view.packet?.packet.execution.anticipatedConsequences.map((item, index) => (
              <li key={`${item.kind}-${index}`}>{item.description}</li>
            ))}
          </ul>
        </MissionGroup>
      )}
    </div>
  );
}

/**
 * Said on the run when the build had no install step to run. Kept as an alias
 * of the environment line's "none" wording so the record says it once.
 */
export const BUILT_WITHOUT_ENVIRONMENT = BUILD_ENVIRONMENT_LINES.none;

/**
 * The record's first line: which environment the build installs (or that it
 * is waiting for the repository's setup), from the first second of the build,
 * moving on as the latest environment event changes. Nothing when the run
 * recorded no environment.
 */
export function BuildEnvironmentLine({ view }: { view: WorkMissionView }) {
  const record = buildEnvironmentRecord(view);
  if (!record) return null;
  return (
    <p className="rp-env-note" data-testid="mission-build-environment" data-source={record.source}>
      {buildEnvironmentLine(record)}
    </p>
  );
}

/** Does the record carry a plan worth printing? A packet plan or a legacy builder plan. */
export function hasRecordedPlan(view: WorkMissionView | null): boolean {
  return view != null && (view.plan != null || view.legacyPlan != null);
}

/**
 * What the run record adds to the live card on the run page: the Receipt and
 * the outputs a mission build revised, each as one more fold. Only what the
 * record actually holds renders. A planning run has no receipt and no revised
 * output, so it gets no folds rather than a row of "not recorded" panels,
 * which is what the tabbed record used to print for every one of them.
 */
export function RunRecordFolds({
  view,
  loading = false,
  error = false,
  onRetry,
}: {
  view: WorkMissionView | null;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}) {
  if (!view) {
    if (loading) {
      return <p className="cf-mission-empty" role="status">Loading the record…</p>;
    }
    if (error) {
      return (
        <p className="cf-mission-empty" role="alert">
          The work record could not be loaded.
          {onRetry ? (
            <>
              {" "}
              <button type="button" className="cf-mission-retry" onClick={onRetry}>
                Retry
              </button>
            </>
          ) : null}
        </p>
      );
    }
    return null;
  }
  // Legacy outputs are already on the card's own Outputs fold; only a mission
  // build's revised outputs carry more than that fold shows.
  const revised = view.outputs.filter((output) => output.revision != null);
  return (
    <>
      {/* The environment line opens the record: the build writes its
          environment event before the sandbox boots. */}
      <BuildEnvironmentLine view={view} />
      <SkillsLoadedFold view={view} />
      {/* The backend makes one "Legacy Run finished" receipt up for every
          finished run without a packet, planning runs included. That is the
          placeholder this page stopped printing, so only a receipt a mission
          build actually recorded earns the fold. */}
      {view.source === "mission" && view.receipts.some((entry) => entry.sourceKind === "run_receipt") && (
        <ReceiptFold view={view} />
      )}
      {revised.length > 0 && (
        <Fold
          testId="run-record-outputs"
          word="Outputs in detail"
          rest={`${revised.length} revised output${revised.length === 1 ? "" : "s"}`}
          cap=""
          open
        >
          <div className="cf-mission-outputs" data-testid="mission-outputs">
            {revised.map((output) => <ArtifactCard key={output.sourceId} output={output} />)}
          </div>
        </Fold>
      )}
    </>
  );
}

/**
 * Which skills, plugins and rules the run loaded (YAG-784): each source at the
 * commit it was read, or why it was not loaded. Recorded at run start, so it
 * shows before the delivery Receipt exists. Nothing when none were recorded.
 */
function SkillsLoadedFold({ view }: { view: WorkMissionView }) {
  const lines = (Array.isArray(view.agentSources) ? view.agentSources : [])
    .filter((source) => isObject(source) && typeof source.fullName === "string")
    .map(agentSourceLine);
  if (lines.length === 0) return null;
  return (
    <Fold
      testId="run-record-skills"
      word={SKILLS_LOADED_LABEL}
      rest={`${lines.length} source${lines.length === 1 ? "" : "s"}`}
      cap=""
      open
    >
      <ul className="cf-mission-list" data-testid="mission-skills-loaded">
        {lines.map((line, index) => <li key={index}>{line}</li>)}
      </ul>
    </Fold>
  );
}

function ReceiptFold({ view }: { view: WorkMissionView }) {
  // The newest receipt names the fold; the backend lists them in sequence.
  const newest = view.receipts[view.receipts.length - 1]?.receipt ?? {};
  const newestProjection = isObject(newest.projection) ? newest.projection : newest;
  const newestSnapshot = isObject(newestProjection.snapshot) ? newestProjection.snapshot : null;
  const recordedAt =
    stringValue(newest.constructedAt) ??
    stringValue(newestProjection.constructedAt) ??
    stringValue(newest.createdAt);
  const sourceRefs = view.receipts.flatMap(({ receipt }) =>
    Array.isArray(receipt.sourceRefs) ? receipt.sourceRefs.filter(isObject) : [],
  );
  const consequences = view.outputs.flatMap((output) =>
    (output.revision?.consequenceIds ?? []).map((id) => ({ id, title: output.artifact.title ?? "Output" })),
  );
  return (
    <Fold
      testId="run-record-receipt"
      word="Receipt"
      rest={(newestSnapshot ? stringValue(newestSnapshot.summary) : null) ?? "recorded"}
      cap={hhmm(recordedAt) ?? ""}
      open
    >
      <div className="cf-mission-stack" data-testid="mission-receipt">
        {view.receipts.map(({ receipt, sourceId }) => {
          const projection = isObject(receipt.projection) ? receipt.projection : receipt;
          const snapshot = isObject(projection.snapshot) ? projection.snapshot : null;
          return (
            <article className="cf-mission-receipt" key={sourceId}>
              <b>{receiptLabel(stringValue(receipt.type) ?? stringValue(projection.kind))}</b>
              {snapshot && <p>{stringValue(snapshot.summary) ?? "The delivery is recorded on the ticket."}</p>}
              <span>{stringValue(receipt.contentHash)?.slice(0, 12) ?? stringValue(projection.missionPacketHash)?.slice(0, 12) ?? "legacy"}</span>
            </article>
          );
        })}
        {sourceRefs.length > 0 && (
          <MissionGroup title="Sources">
            <ul className="cf-mission-sources">
              {sourceRefs.map((source, index) => {
                const url = safeUrl(stringValue(source.url));
                const label = stringValue(source.label) ?? stringValue(source.sourceSystem) ?? `Source ${index + 1}`;
                return <li key={`${label}-${index}`}>{url ? <a href={url} target="_blank" rel="noreferrer">{label}</a> : label}</li>;
              })}
            </ul>
          </MissionGroup>
        )}
        {consequences.length > 0 && (
          <MissionGroup title="External effects">
            <ul className="cf-mission-list">
              {consequences.map((consequence) => <li key={consequence.id}>{consequence.title} · {consequence.id}</li>)}
            </ul>
          </MissionGroup>
        )}
        {view.outputs.some((output) => output.revision?.undo) && (
          <MissionGroup title="Recovery">
            <ul className="cf-mission-list">
              {view.outputs.flatMap((output) => output.revision?.undo ? [
                <li key={output.revision.id}>{output.artifact.title ?? "Output"}: {output.revision.undo.kind}{output.revision.undo.expiresAt ? ` until ${formatRelative(output.revision.undo.expiresAt)}` : ""}</li>,
              ] : [])}
            </ul>
          </MissionGroup>
        )}
      </div>
    </Fold>
  );
}

function ArtifactCard({ output }: { output: WorkMissionView["outputs"][number] }) {
  const artifact = output.artifact;
  const revision = output.revision;
  const type = normalizedArtifactType(artifact);
  const title = stringValue(artifact.title) ?? artifactLabel(type);
  const url = safeUrl(stringValue(artifact.url) ?? legacyUrl(artifact));
  return (
    <article className="cf-output" data-artifact-type={type}>
      <header className="cf-output-head">
        <div>
          <span className="cf-output-type">{artifactLabel(type)}</span>
          <h3>{title}</h3>
        </div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer">
            Open <ExternalLink width={12} height={12} aria-hidden />
          </a>
        )}
      </header>
      <div className="cf-output-meta">
        {stringValue(artifact.status) && <span>{stringValue(artifact.status)}</span>}
        {revision && <span>Revision {revision.revision}</span>}
        {revision?.providerVersion && <span>Provider version {revision.providerVersion}</span>}
      </div>
      {revision?.preview && type !== "candidate_bundle" && <ArtifactPreview preview={revision.preview} />}
      <TypeDetails type={type} artifact={artifact} revision={revision} />
      {revision && <VerificationList verification={revision.verification} />}
      {revision && revision.citations.length > 0 && (
        <MissionGroup title="Sources">
          <ul className="cf-mission-sources">
            {revision.citations.map((citation) => {
              const citationUrl = safeUrl(citation.sourceRef.url);
              const label = citation.sourceRef.label ?? citation.sourceRef.sourceSystem;
              return (
                <li key={citation.id}>
                  {citationUrl ? <a href={citationUrl} target="_blank" rel="noreferrer">{label}</a> : label}
                  {citation.locator ? ` · ${citation.locator}` : ""}
                  {citation.claim ? <span>{citation.claim}</span> : null}
                </li>
              );
            })}
          </ul>
        </MissionGroup>
      )}
    </article>
  );
}

function ArtifactPreview({ preview }: { preview: NonNullable<MissionArtifactRevision["preview"]> }) {
  if (preview.kind === "markdown" && typeof preview.data === "string") {
    return <Markdown className="cf-output-preview" text={preview.data} />;
  }
  if (preview.kind === "link") {
    const url = safeUrl(typeof preview.data === "string" ? preview.data : null);
    return url ? <a className="cf-output-preview-link" href={url} target="_blank" rel="noreferrer">Open preview</a> : null;
  }
  if (preview.kind === "table") return <TablePreview data={preview.data} />;
  return <pre className="cf-output-json">{safeJson(preview.data)}</pre>;
}

function TablePreview({ data }: { data: unknown }) {
  const rows = Array.isArray(data) ? data : isObject(data) && Array.isArray(data.rows) ? data.rows : [];
  const cells = rows.filter(Array.isArray).slice(0, 8) as unknown[][];
  if (cells.length === 0) return <pre className="cf-output-json">{safeJson(data)}</pre>;
  return (
    <div className="cf-output-table-wrap">
      <table className="cf-output-table">
        <tbody>{cells.map((row, rowIndex) => (
          <tr key={rowIndex}>{row.slice(0, 8).map((cell, columnIndex) => <td key={columnIndex}>{displayValue(cell)}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function TypeDetails({ type, artifact, revision }: { type: string; artifact: MissionArtifact; revision: MissionArtifactRevision | null }) {
  const previewData = isObject(revision?.preview?.data) ? revision.preview.data : {};
  const values: Array<[string, unknown]> = [];
  if (type === "candidate_bundle" || type === "pull_request") {
    values.push(["Base", previewData.baseSha ?? artifact.baseSha], ["Tree", previewData.treeSha ?? artifact.treeSha]);
  }
  if (type === "candidate_bundle") {
    const humanReview = revision?.verification.find(
      (check) => check.observer === "reviewer" || check.verifierProfile.includes("human"),
    );
    values.push([
      "Human review",
      humanReview?.status === "passed" ? "Human verified (advisory)" : "Not recorded",
    ]);
  }
  if (type === "document") values.push(["Sharing", previewData.sharingState ?? artifact.sharingState]);
  if (type === "spreadsheet") {
    values.push(["Range", previewData.range ?? artifact.range], ["Rows", previewData.rowCount ?? artifact.rowCount]);
  }
  if (type === "draft_action") values.push(["Recipient", previewData.recipient ?? artifact.recipient]);
  const present = values.filter(([, value]) => value !== null && value !== undefined && value !== "");
  const diffPreview = type === "candidate_bundle" && typeof previewData.diffPreview === "string"
    ? previewData.diffPreview
    : null;
  if (present.length === 0 && !diffPreview) return null;
  return (
    <>
      {present.length > 0 && <InfoGrid rows={present.map(([label, value]) => [label, displayValue(value)])} />}
      {diffPreview && (
        <MissionGroup title="Candidate diff">
          <pre className="cf-output-diff">{diffPreview}</pre>
          {previewData.diffPreviewTruncated === true && (
            <p className="cf-mission-note">This preview leaves out part of the diff. The whole diff is stored with the candidate.</p>
          )}
        </MissionGroup>
      )}
    </>
  );
}

function VerificationList({ verification }: { verification: MissionVerification[] }) {
  if (verification.length === 0) return <Callout tone="neutral">Verification has not been recorded.</Callout>;
  return (
    <MissionGroup title="Verification">
      <div className="cf-verification">
        {verification.map((check, index) => (
          <div className={`cf-verification-row is-${check.status}`} key={check.id ?? `${check.criterionId}-${index}`}>
            <span>{verificationStatus(check.status)}</span>
            <div><b>{check.summary}</b><small>{check.kind.replaceAll("_", " ")} · {check.authority}</small></div>
          </div>
        ))}
      </div>
    </MissionGroup>
  );
}

function MissionGroup({ title, children }: { title: string; children: ReactNode }) {
  return <section className="cf-mission-group"><h4>{title}</h4>{children}</section>;
}

function InfoGrid({ rows }: { rows: Array<[string, ReactNode]> }) {
  return <dl className="cf-mission-grid">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>;
}

function Callout({ tone, children }: { tone: "warn" | "neutral"; children: ReactNode }) {
  return <p className={`cf-mission-callout is-${tone}`}>{children}</p>;
}

function isObject(value: unknown): value is JsonObject {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function safeUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin === window.location.origin && (url.protocol === "https:" || url.protocol === "http:")) {
      return url.href;
    }
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function legacyUrl(artifact: MissionArtifact): string | null {
  for (const key of ["prUrl", "documentUrl", "spreadsheetUrl", "pageUrl", "webUrl"]) {
    const value = stringValue(artifact[key]);
    if (value) return value;
  }
  return null;
}

function normalizedArtifactType(artifact: MissionArtifact): string {
  const raw = stringValue(artifact.type) ?? stringValue(artifact.kind) ?? "report";
  const aliases: Record<string, string> = {
    pr: "pull_request",
    doc: "document",
    knowledge_page: "library_page",
    decision_draft: "draft_action",
    gmail_drafts: "draft_action",
  };
  return aliases[raw] ?? raw;
}

function artifactLabel(type: string): string {
  const labels: Record<string, string> = {
    pull_request: "Draft pull request",
    candidate_bundle: "Code candidate",
    report: "Report",
    document: "Google Doc",
    spreadsheet: "Google Sheet",
    library_page: "Page",
    draft_action: "Draft action",
    configuration: "Configuration",
    dataset: "Dataset",
  };
  return labels[type] ?? type.replaceAll("_", " ");
}

function stageLabel(stage: string): string {
  const labels: Record<string, string> = {
    context: "Preparing context",
    plan: "Planning",
    gather: "Gathering evidence",
    build: "Building the draft",
    check: "Checking the work",
    deliver: "Ready for review",
  };
  return labels[stage] ?? stage.replaceAll("_", " ");
}

function stepStatus(status: string): string {
  const labels: Record<string, string> = { succeeded: "Done", running: "Working", waiting: "Waiting on you", failed: "Failed", cancelled: "Stopped", unknown: "Checking", reconciling: "Checking" };
  return labels[status] ?? "Planned";
}

function capabilityLabel(value: string): string {
  return value.split(/[.:/]/).filter(Boolean).at(-1)?.replaceAll("_", " ") ?? value;
}

function verificationStatus(status: MissionVerification["status"]): string {
  return status === "passed" ? "Passed" : status === "failed" ? "Failed" : status === "inconclusive" ? "Inconclusive" : "Not run";
}

function receiptLabel(kind: string | null): string {
  if (!kind) return "Receipt";
  return `${kind.charAt(0).toUpperCase()}${kind.slice(1).replaceAll("_", " ")} Receipt`;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return safeJson(value);
}

function safeJson(value: unknown): string {
  try { return JSON.stringify(value, null, 2); } catch { return "Unavailable"; }
}
